// components/participantes/CallHistoryModal.jsx
import { Modal, Card, Tag, Row, Col, Divider, Empty, Spin, Tooltip, Badge } from "antd";
import {
  FaPhone, FaCalendar, FaUser, FaMapMarkerAlt, FaCheck,
  FaTimes, FaInfoCircle, FaClock, FaUserCheck, FaUserTimes,
  FaPhoneSlash, FaBan, FaHistory, FaStickyNote, FaExclamationTriangle, FaQuestionCircle
} from "react-icons/fa";
import { ExportToWord } from './ExportToWord';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

export function CallHistoryModal({
  open,
  onClose,
  participant,
  calls,
  loading = false
}) {
  if (!participant) return null;

  // Función para obtener etiqueta del intento
  const getIntentoLabel = (numero) => {
    const labels = {
      1: "Primera Llamada",
      2: "Segunda Llamada",
      3: "Tercera Llamada"
    };
    return labels[numero] || `Llamada ${numero}`;
  };

  // Función para obtener color del estado
  const getEstadoColor = (estadoId) => {
    switch (estadoId) {
      case 1: return 'blue';   // EN_PROCESO
      case 2: return 'green';  // VERIFICADO
      case 3: return 'red';    // NO_CONTINUA
      case 4: return 'gray';   // NO_VERIFICADO
      default: return 'default';
    }
  };

  // Función para obtener icono del estado
  const getEstadoIcon = (estadoId) => {
    switch (estadoId) {
      case 1: return <FaClock style={{ color: '#1890ff' }} />;
      case 2: return <FaUserCheck style={{ color: '#52c41a' }} />;
      case 3: return <FaUserTimes style={{ color: '#ff4d4f' }} />;
      default: return <FaInfoCircle />;
    }
  };

  // Función para obtener icono del subestado
  const getSubestadoIcon = (subestadoId) => {
    switch (subestadoId) {
      case 1: return <FaPhoneSlash style={{ color: '#ffa940' }} />; // FUERA_SERVICIO
      case 2: return <FaPhone style={{ color: '#ffa940' }} />;      // NUMERO_OCUPADO
      case 3: return <FaBan style={{ color: '#ffa940' }} />;        // RECHAZADA
      case 4: return <FaUserTimes style={{ color: '#ff4d4f' }} />;  // CONTACTO_ERRONEO
      default: return null;
    }
  };

  // Función para obtener icono de elegibilidad
  const getElegibilidadIcon = (esElegible) => {
    return esElegible ? (
      <FaCheck style={{ color: '#52c41a' }} />
    ) : (
      <FaTimes style={{ color: '#ff4d4f' }} />
    );
  };

  // Función para formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return 'No registrada';
    return dayjs(fecha).format('DD/MM/YYYY HH:mm');
  };

  // Información resumen del participante
  const getResumenParticipante = () => {
    const totalLlamadas = calls?.length || 0;
    const ultimaLlamada = calls?.[calls.length - 1];
    const estadoActual = ultimaLlamada?.estadoLlamadaNombre || 'Sin llamadas';

    return {
      totalLlamadas,
      estadoActual,
      estadoActualId: ultimaLlamada?.estadoLlamadaId,
      ultimaFecha: ultimaLlamada?.fechaContacto || 'No disponible'
    };
  };

  const resumen = getResumenParticipante();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ maxWidth: 1200 }}
      centered
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: '#1890ff',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FaHistory style={{ fontSize: 20, color: "white" }} />
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
              Historial Completo de Llamadas
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {participant.nombres} {participant.apellidos} • {participant.num_identificacion}
            </div>
          </div>
          {!loading && calls && calls.length > 0 && (
            <ExportToWord participant={participant} calls={calls} />
          )}
        </div>
      }
    >
      {/* Información del Participante */}
      <Card
        size="small"
        style={{
          marginBottom: 24,
          borderLeft: '4px solid #1890ff',
          background: '#f8fbff'
        }}
      >
        <Row gutter={[16, 8]}>
          <Col span={8}>
            <strong>Nombre completo:</strong>
            <div style={{ color: '#666' }}>
              {participant.nombres} {participant.apellidos}
            </div>
          </Col>
          <Col span={8}>
            <strong>Documento:</strong>
            <div style={{ color: '#666' }}>
              {participant.tipo_documento_nombre} {participant.num_identificacion}
            </div>
          </Col>
          <Col span={8}>
            <strong>Teléfono:</strong>
            <div style={{ color: '#666' }}>{participant.telefono || 'No registrado'}</div>
          </Col>
          <Col span={8}>
            <strong>Correo:</strong>
            <div style={{ color: '#666' }}>{participant.correo || 'No registrado'}</div>
          </Col>
          <Col span={8}>
            <strong>Ubicación:</strong>
            <div style={{ color: '#666' }}>
              {participant.municipio}, {participant.departamento}
            </div>
          </Col>
          <Col span={8}>
            <strong>Estado actual:</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {getEstadoIcon(resumen.estadoActualId)}
              <Tag color={getEstadoColor(resumen.estadoActualId)}>
                {resumen.estadoActual}
              </Tag>
              <Badge
                count={resumen.totalLlamadas}
                style={{ backgroundColor: '#1890ff' }}
                title={`${resumen.totalLlamadas} llamadas realizadas`}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Lista de Llamadas */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Cargando historial de llamadas...</div>
        </div>
      ) : !calls || calls.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No se han registrado llamadas para este participante"
        />
      ) : (
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px' }}>
          {calls.map((call, index) => (
            <CallHistoryCard
              key={call.id || index}
              call={call}
              getIntentoLabel={getIntentoLabel}
              getEstadoIcon={getEstadoIcon}
              getEstadoColor={getEstadoColor}
              getSubestadoIcon={getSubestadoIcon}
              getElegibilidadIcon={getElegibilidadIcon}
              formatFecha={formatFecha}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

// Componente separado para la tarjeta de llamada
const CallHistoryCard = ({
  call,
  getIntentoLabel,
  getEstadoIcon,
  getEstadoColor,
  getSubestadoIcon,
  getElegibilidadIcon,
  formatFecha
}) => {
  // Determinar si tiene información de elegibilidad relevante
  const tieneInformacionElegibilidad = () => {
    // Solo mostrar elegibilidad para estados VERIFICADO (2), NO_CONTINUA (3) o CONTACTO_ERRONEO (4)
    const estadosConElegibilidad = [2, 3];
    const esContactoErroneo = call.estadoLlamadaId === 1 && call.subestadoLlamadaId === 4;

    return estadosConElegibilidad.includes(call.estadoLlamadaId) || esContactoErroneo;
  };

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderLeft: `4px solid ${getEstadoColor(call.estadoLlamadaId)}`,
        transition: 'all 0.2s ease',
        background: call.esElegible === false ? '#fffbfb' : 'white'
      }}
      hoverable
    >
      {/* Header de la llamada */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: call.esElegible === false ? '#ffccc7' :
              call.esElegible === true ? '#b7eb8f' : '#f0f0f0',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FaPhone style={{
              color: call.esElegible === false ? '#ff4d4f' :
                call.esElegible === true ? '#52c41a' : '#666'
            }} />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {getIntentoLabel(call.numeroIntento)}
            </div>
            <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaCalendar size={12} />
              {formatFecha(call.fechaContacto)}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {getEstadoIcon(call.estadoLlamadaId)}
          <Tag color={getEstadoColor(call.estadoLlamadaId)}>
            {call.estadoLlamadaNombre}
          </Tag>
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Detalles de la llamada */}
      <Row gutter={[16, 12]}>
        {/* Subestado si existe */}
        {call.subestadoLlamadaId && (
          <Col span={24}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 12,
              padding: '8px',
              background: '#fafafa',
              borderRadius: 6
            }}>
              <div style={{ marginTop: 2 }}>
                {getSubestadoIcon(call.subestadoLlamadaId)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  Detalle del contacto:
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{call.subestadoLlamadaNombre}</span>
                  {call.subestadoLlamadaDescripcion && (
                    <Tooltip title={call.subestadoLlamadaDescripcion}>
                      <FaInfoCircle style={{ color: '#999', cursor: 'pointer' }} />
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </Col>
        )}

        {/* Elegibilidad - Solo mostrar si aplica */}
        {tieneInformacionElegibilidad() && call.esElegible !== null && call.esElegible !== undefined && (
          <Col span={24}>
            <div style={{
              padding: '12px',
              background: call.esElegible ? '#f6ffed' : '#fff2f0',
              borderRadius: 6,
              border: `1px solid ${call.esElegible ? '#b7eb8f' : '#ffccc7'}`
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>

                {/* FILA SUPERIOR */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  {getElegibilidadIcon(call.esElegible)}
                  <strong style={{ fontSize: 14, marginBottom: 4 }}>
                    Evaluación de elegibilidad:
                  </strong>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: call.esElegible ? '#52c41a' : '#ff4d4f'
                    }}
                  >
                    {call.esElegible ? 'Sí es elegible' : 'No es elegible'}
                  </span>
                </div>
                {call.motivoNoElegibleNombre && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <FaExclamationTriangle style={{ color: '#ff4d4f' }} />
                      <strong>Motivo:</strong>
                      <span>{call.motivoNoElegibleNombre}</span>
                    </div>
                  </div>
                )}


                {(call.motivoOtroInfoComprobable || call.motivoOtrTresIntentos || call.motivoOtroCual) && (
  <div style={{ marginTop: 12, marginBottom: 8 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
      <FaExclamationTriangle style={{ color: '#ff4d4f', marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#000000' }}>
          Motivos específicos seleccionados:
        </div>
        
       
          {call.motivoOtroInfoComprobable && (
            <span style={{ fontWeight: 500, marginBottom: 4 }}>Información no comprobable</span>
          )}
          
          {call.motivoOtrTresIntentos && (
            <span style={{ fontWeight: 500, marginBottom: 4 }}>Tres intentos sin respuesta</span>
          )}
          
          {call.motivoOtroCual && (
            <div style={{ fontWeight: 500, marginBottom: 4 }}>¿Cuál? : {call.motivoOtroOtro || ' No especificado'}</div>
          )}
       
      </div>
    </div>
  </div>
)}

                {!call.esElegible && call.observacionNoElegible && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <FaStickyNote style={{ color: '#ff4d4f' }} />
                      <strong>Observación:</strong>
                      {call.observacionNoElegible}
                    </div>
                  </div>
                )}

                {call.departamentoNombre && call.municipioNombre && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px',
                    background: 'white',
                    borderRadius: 4,
                    border: '1px dashed #ffa940'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FaMapMarkerAlt style={{ color: '#ff4d4f' }} />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>Ubicación reportada:</div>
                        <div style={{ color: '#666' }}>
                          {call.municipioNombre}, {call.departamentoNombre}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Col>
        )}

        {/* Observación general */}
        {call.observacionGeneral && (
          <Col span={24}>
            <div style={{
              padding: '12px',
              background: '#f9f9f9',
              borderRadius: 6,
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <FaStickyNote style={{ color: '#722ed1', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#000000ff' }}>
                    Observación general del beneficiario:
                  </div>
                  <div style={{
                    color: '#595959',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-line'
                  }}>
                    {call.observacionGeneral}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        )}

        {/* Información de registro */}
        <Col span={24}>
          <Divider style={{ margin: '12px 0' }} />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#8c8c8c'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaUser size={10} />
              <span>Registrado por: <strong>{call.usuarioId || 'Sistema'}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaCalendar size={10} />
              <span>Registro: <strong>{formatFecha(call.createdAt)}</strong></span>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};